/**
 * Feldzuordnung der PB-Profilmaske: welcher Formular-Key in welche Tabelle
 * geschrieben wird.
 *
 * Einzige Quelle für beide Formulare, die `FormSections` rendern:
 *   - `PbProfileEdit`      (Profildaten-Tab eines bestehenden PB)
 *   - `FamilyMemberForm`   (Person im Familienkonto anlegen/bearbeiten)
 *
 * Grund für die Zusammenlegung: `FamilyMemberForm` rendert dieselben ~75
 * Felder, baute seinen Payload aber aus einer eigenen 17-Felder-Liste. Alles
 * andere wurde beim Anlegen still verworfen und war nach dem Neuladen weg.
 * Eine geteilte Liste hält die beiden Pfade zwangsläufig synchron.
 */
import type { PbFormState } from './sections';

/* ─── Field ownership: which form key writes to which table ────────────── */
export const USER_FIELDS = [
  'status', 'konto', 'invisible', 'startdatum', 'todestag',
  // 'zusage_erhalten_am' / 'mustervertrag_versendet_am' werden inline im
  // Phasen-Block (PhaseRulesBlock) gespeichert. 'anamnese_termin_am' /
  // 'beratungsgespraech_am' werden zusätzlich (fällt die Bedingung der
  // nächsten Stage weg, z.B. letzte Stage) in der Termine-Sektion
  // (sections.tsx TermineSection) sofort gespeichert. Alle vier sind bewusst
  // NICHT im Save-Payload, damit das Formular dortige Änderungen nicht mit
  // veralteten Werten überschreibt (Muster wie 'assigned_to').
  'email', 'mobile', 'telefon', 'strasse', 'hausnummer', 'plz', 'ort', 'land',
  'kontoinhaber', 'bankname', 'iban', 'wage',
  // 'assigned_to' is managed inline in the PbSidebar identity card (saved
  // directly), so it is intentionally NOT part of the Profildaten save payload
  // to avoid the form overwriting a sidebar reassignment with a stale value.
  // 'group_id' wird vom Legacy-Controller nach jedem Save aus assigned_to neu
  // abgeleitet (UserController::updateUser) — ein gesendeter Wert ginge
  // ohnehin verloren.
  // 'locale', 'german_skills' und 'description' werden im PB-Formular nicht
  // mehr angezeigt (aus 12 Sonstiges entfernt) und deshalb auch nicht mit dem
  // beim Öffnen geladenen Stand zurückgeschrieben.
  'badge', 'impfstatus',
] as const;

export const PROFILE_FIELDS = [
  // Familienkonto-Rolle (jetzt fillable auf dem Profile-Datensatz).
  // profiltyp/familiengrad = Label-Strings (Backend validiert gegen
  // profile_options.value), haushalt = bool.
  'profiltyp', 'familiengrad', 'haushalt',
  // Expliziter Pflegeempfänger-Schalter (WP2). Muss mitgeschickt werden, sonst
  // lässt sich ein Profil nie wieder auf „Kontaktperson" zurückstufen.
  'is_pflegeempfaenger',
  'anrede', 'titel', 'vorname', 'nachname', 'geburtsdatum', 'nationalitaet',
  'groesse', 'gewicht', 'familienstand', 'raucher',
  'krankenkasse', 'krankenkasse_typ', 'kranken_vnr', 'sozial_vnr', 'renten_vnr',
  'steuer_id', 'lbnr', 'sozialamt_aufstockung',
  // Batch-12 — „04 Pflegedienst" (SGB5)
  'sgb5', 'sgb5_medigabe', 'sgb5_medigabe_taeglich', 'sgb5_ats',
  'pflegegrad', 'beweglichkeit', 'einschraenkungen', 'sonstige_einschraenkungen',
  'besserung', 'sinneswahrnehmung', 'geisteszustand', 'schlafverhalten',
  'grundpflege', 'hygiene', 'ankleiden', 'essen', 'urinkontrolle', 'stuhlkontrolle',
  'wohnsituation', 'wohnort', 'wohnzustand', 'haustiere', 'haustiere_welche',
  'einkaufsmoeglichkeiten', 'charaktereigenschaften', 'ausgepraegtes_familienleben',
  'bk_anrede', 'bk_aktivitaeten', 'bk_deutsch', 'bk_fuehrerschein', 'bk_raucher',
  'hobbys', 'umfeld', 'sonstiges',
  'freitext_sis_1', 'freitext_sis_2', 'freitext_sis_3', 'freitext_sis_4', 'freitext_sis_5',
  'freitext_sis_6', 'freitext_sis_7', 'freitext_sis_8', 'freitext_sis_9',
  // Schalter der Sektionen 02–07 (JSON-Objekt, s. sectionToggles.ts)
  'sektionen_aktiv',
] as const;

// Vertrag/Preise laufen NICHT über den Profil-Save: Vertragsanpassung und
// Ausgleichsbetrag legen eigene Datensätze an (sections.tsx PbVertragBlock).

/**
 * Felder, die der v2-Member-Endpunkt selbst behandelt (eigene Validierung,
 * Pflegegrad-Mapping Level ⇄ Options-ID) und die deshalb nicht noch einmal
 * über die Zusatzfeld-Listen mitgeschickt werden dürfen.
 */
export const MEMBER_CORE_FIELDS = [
  'profiltyp', 'familiengrad', 'haushalt', 'is_pflegeempfaenger', 'pflegegrad',
  'anrede', 'titel', 'vorname', 'nachname',
  'email', 'mobile', 'telefon', 'strasse', 'hausnummer', 'plz', 'ort', 'land',
  // Werden im Familienkonto nicht gepflegt (Pipeline-/Systemfelder).
  'status', 'konto', 'group_id',
] as const;

const CORE = new Set<string>(MEMBER_CORE_FIELDS);

/** Profil-Zusatzfelder für den v2-Member-Payload (ohne Kernfelder). */
export const MEMBER_EXTRA_PROFILE_FIELDS =
  PROFILE_FIELDS.filter(k => !CORE.has(k)) as readonly (keyof PbFormState)[];

/** User-Zusatzfelder für den v2-Member-Payload (ohne Kernfelder). */
export const MEMBER_EXTRA_USER_FIELDS =
  USER_FIELDS.filter(k => !CORE.has(k)) as readonly (keyof PbFormState)[];

/** Pick a subset of form fields → plain object (omitting undefined). */
export function pickPayload(form: PbFormState, keys: readonly (keyof PbFormState)[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const v = form[k];
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Pflegegrad als Level 1–5 für die v2-API.
 *
 * Im Formular gibt es zwei Quellen: den Kopfblock (`pflegegrad_level`, bereits
 * Level 1–5) und die Pflege-Sektion (`pflegegrad`, die profile_options-ID
 * 5–9). Vorher las der Payload nur den Kopfblock — wer den Pflegegrad in der
 * Sektion setzte (das dort als Pflichtfeld markierte Control), speicherte
 * `null`.
 */
export function pflegegradLevel(form: PbFormState): number | null {
  const raw = form.pflegegrad ?? form.pflegegrad_level;
  if (raw === undefined || raw === null || raw === '') return null;
  const v = Number(raw);
  if (!Number.isFinite(v)) return null;
  if (v >= 5 && v <= 9) return v - 4;   // profile_options-ID → Level
  return v >= 1 && v <= 5 ? v : null;
}

/**
 * Paket A (A11) — Pflegegrad-Level (1–5) aus einem beliebigen Rohwert.
 *
 * Führende Quelle ist `pflegegrad_level` (0–5). Fehlt es, wird aus dem
 * Legacy-Feld `profiles.pflegegrad` abgeleitet, das entweder schon das Level
 * (1–5) oder die profile_options-ID (5–9, +4-Offset) enthält. 0, leer oder
 * unbekannt → null („kein PG").
 */
export function pflegegradLevelFromRaw(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const v = Number(raw);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v >= 5 && v <= 9) return v - 4;   // profile_options-ID → Level
  return v >= 1 && v <= 5 ? v : null;
}

/**
 * Pflegegrad-Level für die Anzeige (Sidebar-Pill): bevorzugt
 * `pflegegrad_level` aus Profil oder User, sonst Ableitung aus `pflegegrad`.
 */
export function displayPflegegradLevel(
  profile: Record<string, unknown> | null | undefined,
  user?: Record<string, unknown> | null,
): number | null {
  const explicit = profile?.pflegegrad_level ?? user?.pflegegrad_level;
  if (explicit !== undefined && explicit !== null && explicit !== '') {
    return pflegegradLevelFromRaw(explicit);
  }
  return pflegegradLevelFromRaw(profile?.pflegegrad ?? user?.pflegegrad);
}
